import inheritedFromPhrase from '@UI/Pages/AiAssignments/Shared/Sidebars/UsageSettingsSidebar/inheritedFromPhrase';
import type UsageSettingView from '@UI/Store/AiConfiguration/Views/UsageSettingView';
import type { TextBounds } from '@UI/Store/AiConfiguration/Views/UsageSettingsView';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import * as React from 'react';
import { Switch, TextArea } from '@verclam/ui-kit';

const conflictBlockStyle = css`
	margin: 0;
	padding: 0.625rem 0.875rem;
	border-radius: ${theme.radii.small};
	background-color: ${theme.states.warning.surface};
	color: ${theme.states.warning.ink};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
`;

type Props = {
	readonly name: string;
	readonly label: string;
	readonly helpText: string;
	/** `formData.includesAccountOnlyFields`, threaded down rather than re-read from `scope.type`: the
	 * FormData already computed it once, in `fromView`. */
	readonly isAccountLevel: boolean;
	readonly setting: UsageSettingView<string, TextBounds>;
	readonly isOverridden: boolean;
	readonly value: string;
	readonly onOverriddenChange: (overridden: boolean) => void;
	readonly onValueChange: (value: string) => void;
	readonly errorMessage: string | undefined;
	readonly isFormFieldValid: boolean;
	readonly isSubmitting: boolean;
};

/**
 * Shared by `contextPrompt` and `systemPrompt`: same shape (`own`/`effective`, no length bounds), same
 * rendering rules (`applicable` → `editable` → `supported`, in that order). The
 * "Personnaliser" switch only ever shows for a setting `editable` outside the account level;
 * `systemPrompt` is never editable there, so it silently never renders one, without this component
 * needing to know why.
 */
const PromptSettingField: React.FC<Props> = ({
	name,
	label,
	helpText,
	isAccountLevel,
	setting,
	isOverridden,
	value,
	onOverriddenChange,
	onValueChange,
	errorMessage,
	isFormFieldValid,
	isSubmitting,
}) => {
	if (!setting.applicable) {
		return null;
	}

	if (!setting.editable) {
		return (
			<TextArea
				name={name}
				label={label}
				value={setting.effective ?? ''}
				readOnly
				subLabel={inheritedFromPhrase(setting.effectiveLevel) ?? 'Aucun réglage d’appel.'}
			/>
		);
	}

	if (!setting.supported) {
		return (
			<React.Fragment>
				<TextArea
					name={name}
					label={label}
					value={setting.own ?? ''}
					disabled
					subLabel={setting.unsupportedReason ?? undefined}
				/>
				{setting.isInConflict ? (
					<p css={conflictBlockStyle}>
						Ce réglage est enregistré mais le moteur affecté ne l’accepte pas. Il ne sera pas
						envoyé.
					</p>
				) : null}
			</React.Fragment>
		);
	}

	const showSwitch = !isAccountLevel;

	if (showSwitch && !isOverridden) {
		return (
			<React.Fragment>
				<Switch
					name={`${name}-override`}
					label="Personnaliser ce réglage"
					checked={isOverridden}
					onChange={onOverriddenChange}
					disabled={isSubmitting}
				/>
				<TextArea
					name={name}
					label={label}
					value={setting.effective ?? ''}
					readOnly
					subLabel={inheritedFromPhrase(setting.effectiveLevel) ?? 'Aucun réglage d’appel.'}
				/>
			</React.Fragment>
		);
	}

	return (
		<React.Fragment>
			{showSwitch ? (
				<Switch
					name={`${name}-override`}
					label="Personnaliser ce réglage"
					checked={isOverridden}
					onChange={onOverriddenChange}
					disabled={isSubmitting}
				/>
			) : null}
			<TextArea
				name={name}
				label={label}
				subLabel={helpText}
				value={value}
				onChange={onValueChange}
				disabled={isSubmitting}
				errorMessage={errorMessage}
				isFormFieldValid={isFormFieldValid}
			/>
		</React.Fragment>
	);
};

export default PromptSettingField;
